import React, { useState, useEffect } from 'react';
import { Phone } from 'lucide-react';
import axios from 'axios';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const { toasts, addToast, removeToast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(user => 
        (user.name && user.name.toLowerCase().includes(term)) ||
        (user.nickname && user.nickname.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar usuários');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser({ ...user });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingUser({
      ...editingUser,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`/api/admin/users/${editingUser.id}`, editingUser, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      addToast('Usuário atualizado com sucesso!', 'success');
      setIsModalOpen(false);
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      addToast(err.response?.data?.error || 'Erro ao atualizar usuário', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && users.length === 0) {
    return <div className="p-6">Carregando usuários...</div>;
  }

  if (error && users.length === 0) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-2xl font-bold mb-6 text-primary">Gerenciar Usuários</h1>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Buscar por nome, apelido ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 min-h-11"
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-[600px] divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Nome</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Apelido</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Contato / PIX</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-3 lg:px-6 py-4 text-sm text-gray-900 break-words">
                  {user.name}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm text-gray-500 break-words">
                  {user.nickname || '-'}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm text-gray-500 break-words">
                  {user.email || '-'}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm text-gray-500">
                  <div className="flex flex-col text-xs break-all">
                    {user.contact ? <span className="flex items-center gap-1"><Phone className="w-3 h-3" aria-hidden="true" /> {user.contact}</span> : null}
                    {user.pix_key ? <span>PIX: {user.pix_key}</span> : null}
                    {!user.contact && !user.pix_key ? '-' : null}
                  </div>
                </td>
                <td className="px-3 lg:px-6 py-4">
                  <div className="flex flex-col gap-1">
                    {user.is_admin ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 w-max">
                        Admin
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 w-max">
                        Usuário
                      </span>
                    )}
                    {user.payment_verified ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-primary w-max">
                        Pago
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 w-max">
                        Pendente
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm font-medium">
                  <button
                    onClick={() => handleEditClick(user)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="6" className="px-3 lg:px-6 py-4 text-center text-gray-500">
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {isModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Editar Usuário</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    name="name"
                    value={editingUser.name || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Apelido</label>
                  <input
                    type="text"
                    name="nickname"
                    value={editingUser.nickname || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editingUser.email || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Contato</label>
                  <input
                    type="text"
                    name="contact"
                    value={editingUser.contact || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Chave PIX</label>
                  <input
                    type="text"
                    name="pix_key"
                    value={editingUser.pix_key || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Nova Senha (deixe em branco para não alterar)</label>
                  <input
                    type="password"
                    name="password"
                    value={editingUser.password || ''}
                    onChange={handleInputChange}
                    placeholder="Digite a nova senha"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>

                <div className="flex items-center mt-4">
                  <input
                    id="is_admin"
                    name="is_admin"
                    type="checkbox"
                    checked={editingUser.is_admin || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_admin" className="ml-2 block text-sm text-gray-900">
                    Administrador
                  </label>
                </div>

                <div className="flex items-center mt-2">
                  <input
                    id="payment_verified"
                    name="payment_verified"
                    type="checkbox"
                    checked={editingUser.payment_verified || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="payment_verified" className="ml-2 block text-sm text-gray-900">
                    Pagamento Verificado
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {submitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default ManageUsers;